/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * One background job — the same list → view → edit shape as the notification
 * masters: a hero stating what it is, with its actions, then cards for what it
 * runs, when it runs, and what it did.
 *
 * Run now, the job's schedules and its recent runs all live on this page. Before
 * it, answering "what is this job doing?" meant visiting three tabs and
 * filtering each one back down to the same job.
 */
import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Tag } from 'sharedUi/Tag';
import { Button } from 'sharedUi/Button';
import { Dialog } from 'sharedUi/Dialog';
import { Message } from 'sharedUi/Message';
import { InputSwitch } from 'sharedUi/InputSwitch';
import { formatTimestamp } from 'sharedUi/dateUtils';
import { useAppNavigate } from '../../hooks/useAppNavigate';
import { useToastApi } from '../../hooks/use-toast-api';
import { backgroundJobService } from '../../services/background-job.service';
import type { BackgroundJobSchedule } from '../../types/background-job.types';
import { JsonField } from '../../components/common/json-field';
import { JsonView } from '../../components/common/json-view';
import ActiveTag from '../../components/common/active-tag';
import { extractErrorMessage } from '../../utils/error-handler';
import {
  CARD, BackLink, CardHeader, Field, Hero, ActiveBanner, WarnBanner, RecordMeta,
} from '../../components/notifications/engine-view-ui';
import { describeConfig, describeTiming } from './tabs/shared';
import { useJobPermissions } from './tabs/use-job-permissions';
import { groupRuns } from './run-format';
import { RunsCard } from './runs-card';
import { runPath, schedulePath } from './view-paths';

const JOB_LIST = '/background-jobs?tab=jobs';

const TYPE_SEVERITY: Record<string, 'info' | 'warning' | 'success'> = {
  FUNCTION: 'info', API_CALL: 'warning', LLM_PROMPT: 'success',
};

/** How many recent fires the page shows; the full history lives on the dashboard. */
const RECENT_FIRES = 10;

const BackgroundJobView = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useAppNavigate();
  const qc = useQueryClient();
  const { toast, ok, fail } = useToastApi();
  const { canCreate, canUpdate, canExecute, canToggle, canReadRuns } = useJobPermissions();

  const [runOpen, setRunOpen] = useState(false);
  const [runParams, setRunParams] = useState<Record<string, unknown> | undefined>({});

  const jobQ = useQuery({
    queryKey: ['bg-job', id],
    queryFn: () => backgroundJobService.getJob(id!),
    enabled: !!id,
  });

  const schedulesQ = useQuery({
    queryKey: ['bg-schedules', id],
    queryFn: () => backgroundJobService.listSchedules(id!),
    enabled: !!id,
    staleTime: 30_000,
  });

  // Run history is its own grant (BACKGROUND_JOB_RUN.READ). Asking without it
  // is a 403 on every page load, so the card is replaced rather than left to fail.
  const runsQ = useQuery({
    queryKey: ['bg-runs', { jobId: id, view: 'job' }],
    queryFn: () => backgroundJobService.listRuns({
      jobId: id!, page: 1, page_size: 50, sort_by: 'startedAt', sort_order: 'desc',
    }),
    enabled: !!id && canReadRuns,
    staleTime: 15_000,
    refetchInterval: 30_000,
  });
  const groups = useMemo(
    () => groupRuns(runsQ.data?.items ?? []).slice(0, RECENT_FIRES),
    [runsQ.data],
  );

  const runM = useMutation({
    mutationFn: () => backgroundJobService.runNow(id!, runParams),
    onSuccess: (run) => {
      setRunOpen(false);
      qc.invalidateQueries({ queryKey: ['bg-runs'] });
      qc.invalidateQueries({ queryKey: ['bg-activity'] });
      qc.invalidateQueries({ queryKey: ['bg-dashboard'] });
      ok('Run started — this does not shift the schedule');
      // Straight to the run it started: "did it work?" is the question pressing
      // the button raises, and the run's page is where that is answered.
      if (run?._id && canReadRuns) navigate(runPath(run._id));
    },
    onError: (e) => fail(e),
  });

  const toggleM = useMutation({
    mutationFn: ({ scheduleId, on }: { scheduleId: string; on: boolean }) =>
      backgroundJobService.toggleSchedule(scheduleId, on),
    onSuccess: (_d, v) => {
      ok(v.on ? 'Enabled — the next run is computed from now' : 'Disabled — history is preserved');
      qc.invalidateQueries({ queryKey: ['bg-schedules'] });
    },
    onError: (e) => fail(e),
  });

  if (jobQ.isLoading) {
    return <div className="flex min-h-[300px] items-center justify-center"><ProgressSpinner style={{ width: 40, height: 40 }} /></div>;
  }
  if (jobQ.isError || !jobQ.data) {
    return (
      <div className="p-4">
        {toast}
        <Message severity="error" text={extractErrorMessage(jobQ.error, 'Background job not found')} className="mb-4 w-full" />
        <Button label="Back" icon="pi pi-arrow-left" severity="secondary" outlined onClick={() => navigate(JOB_LIST)} />
      </div>
    );
  }

  const job = jobQ.data;
  const live = !job.isDelete;
  const schedules = schedulesQ.data ?? [];
  const timeout = (job.executionConfig as any)?.timeoutSeconds;
  const retention = job.runRetentionDays;

  const scheduleRow = (s: BackgroundJobSchedule) => {
    // a ONCE schedule disables itself after firing — that is "completed", not "turned off"
    const completed = !s.isScheduleEnabled && s.scheduleType === 'ONCE' && !!s.lastRunAt;
    const busy = toggleM.isPending && toggleM.variables?.scheduleId === s._id;
    return (
      <li key={s._id} className="flex flex-wrap items-center gap-2.5 rounded-lg border border-gray-100 bg-gray-50/60 px-3 py-2 dark:border-gray-700 dark:bg-gray-900/30">
        <button type="button" onClick={() => navigate(schedulePath(s))}
          className="text-sm font-medium text-blue-600 hover:underline dark:text-blue-400">
          {s.scheduleName}
        </button>
        <Tag value={s.scheduleType} severity={s.scheduleType === 'CRON' ? 'info' : s.scheduleType === 'INTERVAL' ? 'warning' : 'secondary'} />
        <span className="font-mono text-xs text-gray-600 dark:text-gray-300">{describeTiming(s)}</span>
        {completed && <Tag value="Completed" severity="info" />}
        {!completed && s.isScheduleEnabled && !s.nextRunAt && <Tag value="Won't fire" severity="warning" />}
        <span className="ml-auto text-xs text-ink-muted">
          {s.nextRunAt ? `next ${formatTimestamp(s.nextRunAt)}` : s.lastRunAt ? `last ${formatTimestamp(s.lastRunAt)}` : 'never run'}
        </span>
        <InputSwitch
          checked={!!s.isScheduleEnabled}
          disabled={!canToggle || completed || busy}
          onChange={(e) => toggleM.mutate({ scheduleId: s._id, on: !!e.value })}
        />
        <Button aria-label="View" icon="pi pi-eye" text rounded severity="success" tooltip="View schedule" onClick={() => navigate(schedulePath(s))} />
      </li>
    );
  };

  return (
    <div className="mx-auto space-y-3 p-4" data-testid="SCR-BackgroundJob-View">
      {toast}
      <BackLink label="Background jobs" onClick={() => navigate(JOB_LIST)} />

      <Hero
        icon="settings"
        chip="bg-teal-50 text-teal-600 dark:bg-teal-950/50 dark:text-teal-400"
        code={job.jobKey}
        tags={<>
          <ActiveTag row={job} />
          <Tag value={job.executionType} severity={TYPE_SEVERITY[job.executionType] || 'info'} />
          {job.isDelete && <Tag value="DELETED" severity="danger" />}
        </>}
        description={<>
          <span className="font-medium text-gray-700 dark:text-gray-200">{job.jobName}</span>
          {job.description ? ` — ${job.description}` : ''}
        </>}
        actions={<>
          {canExecute && live && (
            <Button label="Run now" icon="pi pi-play" size="small" severity="success" outlined
              onClick={() => { setRunParams(job.defaultParameters || {}); setRunOpen(true); }} />
          )}
          {canCreate && live && (
            <Button label="Add schedule" icon="pi pi-calendar-plus" size="small" severity="secondary" outlined
              onClick={() => navigate(`/background-jobs/schedules/create?jobId=${job._id}`)} />
          )}
          {canUpdate && live && (
            <Button label="Edit" icon="pi pi-pencil" size="small" severity="secondary"
              onClick={() => navigate(`/background-jobs/jobs/${job._id}/edit`)} />
          )}
        </>}
        banner={job.isDelete
          ? <WarnBanner text="Deleted — it no longer runs. Restore it from the Background Job Master list." />
          : !job.isActive
            ? <WarnBanner text="Inactive — the scheduler skips it. Run now still executes it." />
            : <ActiveBanner text="Active — edits apply from the next run; runs already recorded are unchanged." />}
      />

      <div className={CARD}>
        <CardHeader icon="settings" color="text-teal-500" title="What it runs" hint={job.executionType} />
        <div className="grid grid-cols-1 gap-x-6 gap-y-3.5 px-4 py-4 sm:grid-cols-2">
          <Field label="Runs" mono>{describeConfig(job.executionType, job.executionConfig)}</Field>
          <Field label="Retries">
            {job.maxRetries > 0 ? `${job.maxRetries} × every ${job.retryIntervalSeconds}s` : 'None — a failure is final'}
          </Field>
          <Field label="Timeout">{timeout ? `${timeout}s` : 'Default'}</Field>
          <Field label="Run history kept">
            {retention == null ? "The project's retention window" : retention === 0 ? 'Forever' : `${retention} days`}
          </Field>
          {(job.connectionType || []).length > 0 && (
            <Field label="Connection types">{job.connectionType.join(', ')}</Field>
          )}
        </div>
        <div className="grid grid-cols-1 gap-4 border-t border-gray-100 px-4 py-4 dark:border-gray-700 sm:grid-cols-2">
          <JsonView label="Execution config" value={job.executionConfig} />
          <JsonView label="Default parameters" value={job.defaultParameters} />
        </div>
      </div>

      <div className={CARD}>
        <CardHeader icon="calendar" color="text-indigo-400" title={`Schedules (${schedules.length})`} hint="When the scheduler runs it" />
        <div className="px-4 py-4">
          {schedulesQ.isLoading ? (
            <span className="text-sm text-gray-400">Loading…</span>
          ) : schedules.length ? (
            <ul className="flex flex-col gap-2">{schedules.map(scheduleRow)}</ul>
          ) : (
            <span className="text-sm text-gray-400">No schedule — this job runs only when someone presses Run now.</span>
          )}
        </div>
      </div>

      {canReadRuns ? (
        <RunsCard
          title="Recent runs"
          hint={`Latest ${RECENT_FIRES} fires`}
          groups={groups}
          loading={runsQ.isLoading}
          empty="Nothing has run yet."
        />
      ) : (
        <div className={CARD}>
          <CardHeader icon="history" title="Recent runs" />
          <div className="px-4 py-4 text-sm text-gray-400">
            You can see this job but not its run history — that needs BACKGROUND_JOB_RUN.READ.
          </div>
        </div>
      )}

      <RecordMeta data={job} extra={<>
        <Field label="Job key" mono>{job.jobKey}</Field>
        <Field label="Job name">{job.jobName}</Field>
      </>} />

      {/* Parameters for one manual run. Merged over the job's defaults only — a
          manual run has no schedule, so schedule parameters do not apply. */}
      <Dialog
        visible={runOpen}
        onHide={() => setRunOpen(false)}
        header={`Run ${job.jobKey} now`}
        style={{ width: 560, maxWidth: '95vw' }}
        modal
        footer={
          <div className="flex justify-end gap-2">
            <Button label="Cancel" severity="secondary" outlined onClick={() => setRunOpen(false)} />
            <Button label="Run now" icon="pi pi-play" severity="success"
              onClick={() => runM.mutate()} disabled={runM.isPending} />
          </div>
        }
      >
        <div className="flex flex-col gap-3">
          <JsonField label="Parameters for this run"
            hint="Merged over the job's defaults only — a manual run has no schedule, so schedule parameters do not apply."
            value={runParams}
            onChange={(o) => setRunParams(o || {})} />
          <Message className="w-full justify-start" severity="info"
            text="A manual run takes no schedule claim, so the next scheduled run does not move." />
          {!job.isActive && (
            <Message className="w-full justify-start" severity="warn"
              text="This job is inactive. A manual run executes it anyway; only the scheduler skips inactive jobs." />
          )}
        </div>
      </Dialog>
    </div>
  );
};

export default BackgroundJobView;
